package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.GiverLite
import com.dotdrives.model.Report
import com.dotdrives.model.ReportDefaultUpdate
import com.dotdrives.model.ReportFileItem
import com.dotdrives.model.ReportStatus
import com.dotdrives.model.ReportSummary
import com.dotdrives.model.ReportUpdate
import com.dotdrives.model.ShareAudience
import com.dotdrives.model.Target
import com.dotdrives.repository.ReportRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import com.dotdrives.model.UpdatesBoardPage

data class UpdatesFilters(
    val onlyUnread: Boolean = false,
    val assigneeUserId: String? = null,
    val status: String? = null,
    val targetId: String? = null,
    val query: String = "",
)

data class UpdatesUiState(
    val loading: Boolean = false,
    val reports: List<ReportSummary> = emptyList(),
    val board: UpdatesBoardPage = UpdatesBoardPage(),
    val loadingMore: Boolean = false,
    val filters: UpdatesFilters = UpdatesFilters(),
    val error: String? = null,

    val selectedReportId: String? = null,
    val selectedReport: Report? = null,
    val thread: List<ReportUpdate> = emptyList(),
    val files: List<ReportFileItem> = emptyList(),
    val threadLoading: Boolean = false,
    val threadError: String? = null,

    val draftBody: String = "",
    val posting: Boolean = false,
    val updatingStatus: Boolean = false,

    val targets: List<Target> = emptyList(),
    val defaultUpdates: List<ReportDefaultUpdate> = emptyList(),

    val shareOpen: Boolean = false,
    val shareMode: ShareAudienceMode = ShareAudienceMode.Manual,
    val shareGivers: List<GiverLite> = emptyList(),
    val shareTargetAudience: ShareAudience? = null,
    val shareActiveDrivesAudience: ShareAudience? = null,
    val shareSelectedGiverIds: Set<String> = emptySet(),
    val shareLoading: Boolean = false,
    val sharing: Boolean = false,
    val shareError: String? = null,
) {
    val visibleReports: List<ReportSummary>
        get() = reports.filter { row ->
            (!filters.onlyUnread || row.isUnread) &&
                (filters.assigneeUserId == null || row.assigneeUserId == filters.assigneeUserId) &&
                (filters.status == null || row.status == filters.status) &&
                (filters.targetId == null || row.targetId == filters.targetId) &&
                row.title.contains(filters.query.trim(), ignoreCase = true)
        }

}

/** The three audiences the share dialog offers -- two live-counted presets plus a manual
 *  pick, matching `design/portal-updates.dc.html`'s share dialog. */
enum class ShareAudienceMode { Target, ActiveDrives, Manual }

/**
 * Mirrors `reports._update`'s RLS predicate exactly (docs/schema.md: "Controller ruling:
 * field-role writes" -- `has_perm(org_id,'report.write') or (has_report_write(org_id) and
 * assignee_user_id = auth.uid())`): staff may transition any report; a field rep only their
 * own. The server-side policy is the real enforcement -- this exists so the UI never *offers*
 * a control that would fail it.
 */
fun canTransitionReport(report: Report, currentUserId: String, isStaff: Boolean): Boolean =
    isStaff || report.assigneeUserId == currentUserId

class UpdatesController(
    private val repo: ReportRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val currentUserId: String,
    private val isStaff: Boolean,
) : DotController<UpdatesUiState>(UpdatesUiState(), scope) {
    private var listGeneration = 0L
    private var threadGeneration = 0L

    private var listJob: Job? = null
    private var catalogsLoaded = false

    fun load() = loadPage(false)

    fun loadMore() {
        if (!state.value.loading && !state.value.loadingMore && state.value.board.nextCursor != null) loadPage(true)
    }

    private fun loadPage(more: Boolean) {
        val generation = nextListGeneration()
        listJob?.cancel()
        val current = state.value
        val cursor = if (more) current.board.nextCursor else null
        update {
            it.copy(loading = !more, loadingMore = more, error = null,
                board = if (more) it.board else it.board.copy(nextCursor = null))
        }
        listJob = scope.launch {
            try {
                repo.boardPage(orgId, cursor, current.filters)
                    .onSuccess { page ->
                        if (isCurrentList(generation)) update {
                            it.copy(loading = false, loadingMore = false, board = page,
                                reports = if (more) (it.reports + page.reports).distinctBy { row -> row.id } else page.reports,
                                error = null)
                        }
                    }.onFailure { publishListError(it, generation) }
            } catch (error: Exception) { publishListError(error, generation) }
        }
        if (!catalogsLoaded) {
            catalogsLoaded = true
            scope.launch {
                val targets = async { repo.targets(orgId) }
                val defaults = async { repo.defaultUpdates(orgId) }
                val targetsResult = targets.await()
                val defaultsResult = defaults.await()
                catalogsLoaded = targetsResult.isSuccess && defaultsResult.isSuccess
                update { it.copy(targets = targetsResult.getOrElse { state.value.targets },
                    defaultUpdates = defaultsResult.getOrElse { state.value.defaultUpdates }) }
            }
        }
    }

    fun retry() {
        val reportId = state.value.selectedReportId
        if (reportId != null && state.value.threadError != null) select(reportId) else load()
    }

    fun setFilters(filters: UpdatesFilters) {
        if (state.value.filters == filters) return
        update { it.copy(filters = filters, reports = emptyList(), board = it.board.copy(nextCursor = null)) }
        load()
    }

    fun select(reportId: String) {
        val generation = nextThreadGeneration()
        update {
            it.copy(
                selectedReportId = reportId,
                selectedReport = null,
                thread = emptyList(),
                files = emptyList(),
                threadLoading = true,
                threadError = null,
                draftBody = "",
                shareOpen = false,
                shareGivers = emptyList(),
                shareSelectedGiverIds = emptySet(),
            )
        }
        scope.launch {
            try {
                val reportResult = repo.get(orgId, reportId)
                val threadResult = repo.thread(orgId, reportId)
                val filesResult = repo.files(orgId, reportId)
                if (!isCurrentThread(generation)) return@launch
                reportResult
                    .onSuccess { report ->
                        threadResult
                            .onSuccess { thread ->
                                if (!isCurrentThread(generation)) return@onSuccess
                                update {
                                    if (isCurrentThread(generation)) {
                                        it.copy(
                                            threadLoading = false,
                                            selectedReport = report,
                                            thread = thread,
                                            files = filesResult.getOrElse { emptyList() },
                                            threadError = null,
                                            board = it.board.copy(unreadCount = (it.board.unreadCount - if (it.reports.any { row -> row.id == reportId && row.isUnread }) 1 else 0).coerceAtLeast(0)),
                                            reports = it.reports.map { row ->
                                                if (row.id == reportId) row.copy(isUnread = false) else row
                                            },
                                        )
                                    } else {
                                        it
                                    }
                                }
                                // Best-effort: a failure here leaves the unread dot lit locally
                                // on the next reload, which is a harmless staleness, not worth
                                // surfacing as a thread-load error the retry banner would offer
                                // to "fix" by re-fetching a thread that already loaded fine.
                                repo.markRead(orgId, reportId)
                            }
                            .onFailure { publishThreadError(it, generation) }
                    }
                    .onFailure { publishThreadError(it, generation) }
            } catch (error: Exception) {
                publishThreadError(error, generation)
            }
        }
    }

    fun updateDraft(text: String) {
        update { it.copy(draftBody = text) }
    }

    fun postUpdate() {
        val current = state.value
        val reportId = current.selectedReportId ?: return
        val body = current.draftBody.trim()
        if (body.isEmpty() || current.posting) return
        update { it.copy(posting = true, threadError = null) }
        scope.launch {
            repo.postUpdate(orgId, reportId, body)
                .onSuccess { posted ->
                    update {
                        it.copy(
                            posting = false,
                            draftBody = "",
                            thread = it.thread + posted,
                            reports = it.reports.map { row ->
                                if (row.id == reportId) {
                                    row.copy(latestPreview = posted.body, latestAt = posted.createdAt)
                                } else {
                                    row
                                }
                            },
                        )
                    }
                }
                .onFailure { error ->
                    update { it.copy(posting = false, threadError = error.message ?: "Something went wrong") }
                }
        }
    }

    fun submitForReview() {
        val report = state.value.selectedReport ?: return
        if (!canTransitionReport(report, currentUserId, isStaff)) return
        transitionStatus(report.id, ReportStatus.SUBMITTED)
    }

    /**
     * `report_recipients_insert` and the two audience-preview queries this loads
     * ([ReportRepository.targetAudience]/[ReportRepository.activeDrivesAudience]) are all
     * `has_perm(org_id,'report.write')`-gated server-side, which excludes `field` outright --
     * guarding here (never issuing any of the three fetches, let alone the send) for a
     * field-role viewer mirrors how `GivingSummaryController` never issues its donations query
     * for one.
     */
    fun startShare() {
        if (!isStaff) return
        val report = state.value.selectedReport ?: return
        val targetId = report.targetId
        update {
            it.copy(
                shareOpen = true,
                shareLoading = true,
                shareSelectedGiverIds = emptySet(),
                shareError = null,
                shareTargetAudience = null,
                shareActiveDrivesAudience = null,
            )
        }
        scope.launch {
            val giversResult = repo.givers(orgId)
            val giversError = giversResult.exceptionOrNull()
            if (giversError != null) {
                update { it.copy(shareLoading = false, shareError = giversError.message ?: "Something went wrong") }
                return@launch
            }
            // The two presets are secondary to the always-available manual list above -- a
            // failure here degrades to "no preset audience" (falls back to Manual), not to a
            // blocked dialog, matching how load() treats targets/defaultUpdates as secondary
            // to the primary reports list.
            val targetAudience = targetId?.let { id ->
                repo.targetAudience(orgId, id).getOrElse { ShareAudience(emptyList(), 0) }
            }
            val activeDrivesAudience = repo.activeDrivesAudience(orgId).getOrElse { ShareAudience(emptyList(), 0) }
            val defaultMode = if (targetAudience != null) ShareAudienceMode.Target else ShareAudienceMode.ActiveDrives
            val defaultSelection = when (defaultMode) {
                ShareAudienceMode.Target -> targetAudience?.giverIds.orEmpty().toSet()
                ShareAudienceMode.ActiveDrives -> activeDrivesAudience.giverIds.toSet()
                ShareAudienceMode.Manual -> emptySet()
            }
            update {
                it.copy(
                    shareLoading = false,
                    shareGivers = giversResult.getOrElse { emptyList() },
                    shareTargetAudience = targetAudience,
                    shareActiveDrivesAudience = activeDrivesAudience,
                    shareMode = defaultMode,
                    shareSelectedGiverIds = defaultSelection,
                )
            }
        }
    }

    /** Switching to a preset snaps the selection to that preset's audience (read-only in the
     *  UI); switching to Manual keeps whatever was already individually picked. */
    fun setShareAudience(mode: ShareAudienceMode) {
        update { current ->
            val selection = when (mode) {
                ShareAudienceMode.Target -> current.shareTargetAudience?.giverIds.orEmpty().toSet()
                ShareAudienceMode.ActiveDrives -> current.shareActiveDrivesAudience?.giverIds.orEmpty().toSet()
                ShareAudienceMode.Manual ->
                    if (current.shareMode == ShareAudienceMode.Manual) current.shareSelectedGiverIds else emptySet()
            }
            current.copy(shareMode = mode, shareSelectedGiverIds = selection)
        }
    }

    fun toggleShareGiver(giverId: String) {
        update {
            val next = if (giverId in it.shareSelectedGiverIds) {
                it.shareSelectedGiverIds - giverId
            } else {
                it.shareSelectedGiverIds + giverId
            }
            it.copy(shareMode = ShareAudienceMode.Manual, shareSelectedGiverIds = next)
        }
    }

    fun selectAllShareGivers() {
        update {
            it.copy(
                shareMode = ShareAudienceMode.Manual,
                shareSelectedGiverIds = it.shareGivers.map(GiverLite::id).toSet(),
            )
        }
    }

    fun closeShare() {
        update { it.copy(shareOpen = false, shareError = null) }
    }

    fun confirmShare() {
        if (!isStaff) return
        val current = state.value
        val reportId = current.selectedReportId ?: return
        val giverIds = current.shareSelectedGiverIds.toList()
        if (giverIds.isEmpty() || current.sharing) return
        update { it.copy(sharing = true, shareError = null) }
        scope.launch {
            repo.sendToGivers(orgId, reportId, giverIds)
                .onSuccess {
                    repo.setStatus(orgId, reportId, ReportStatus.SHARED)
                        .onSuccess { updated -> applyStatusUpdate(reportId, updated, sharing = false, shareOpen = false) }
                        .onFailure { error ->
                            update { it.copy(sharing = false, shareError = error.message ?: "Something went wrong") }
                        }
                }
                .onFailure { error ->
                    update { it.copy(sharing = false, shareError = error.message ?: "Something went wrong") }
                }
        }
    }

    private fun transitionStatus(reportId: String, status: String) {
        update { it.copy(updatingStatus = true, threadError = null) }
        scope.launch {
            repo.setStatus(orgId, reportId, status)
                .onSuccess { updated -> applyStatusUpdate(reportId, updated, updatingStatus = false) }
                .onFailure { error ->
                    update { it.copy(updatingStatus = false, threadError = error.message ?: "Something went wrong") }
                }
        }
    }

    private fun applyStatusUpdate(
        reportId: String,
        updated: Report,
        updatingStatus: Boolean = false,
        sharing: Boolean = false,
        shareOpen: Boolean = false,
    ) {
        update {
            val previous = it.reports.firstOrNull { row -> row.id == reportId }?.status ?: it.selectedReport?.status
            fun revisedCount(status: String, count: Int) =
                if (previous == null || previous == updated.status) count else
                    (count - if (previous == status) 1 else 0) + if (updated.status == status) 1 else 0
            it.copy(
                board = it.board.copy(
                    draftCount = revisedCount(ReportStatus.DRAFT, it.board.draftCount),
                    submittedCount = revisedCount(ReportStatus.SUBMITTED, it.board.submittedCount),
                    sharedCount = revisedCount(ReportStatus.SHARED, it.board.sharedCount),
                ),
                updatingStatus = updatingStatus,
                sharing = sharing,
                shareOpen = shareOpen,
                shareSelectedGiverIds = if (!shareOpen) emptySet() else it.shareSelectedGiverIds,
                selectedReport = updated,
                reports = it.reports.map { row -> if (row.id == reportId) row.copy(status = updated.status) else row },
            )
        }
    }

    private fun publishListError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrentList(generation)) return
        update {
            if (isCurrentList(generation)) it.copy(loading = false, loadingMore = false, error = error.message ?: "Something went wrong") else it
        }
    }

    private fun publishThreadError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrentThread(generation)) return
        update {
            if (isCurrentThread(generation)) {
                it.copy(threadLoading = false, threadError = error.message ?: "Something went wrong")
            } else {
                it
            }
        }
    }

    private fun nextListGeneration(): Long {
        listGeneration += 1
        return listGeneration
    }

    private fun isCurrentList(generation: Long): Boolean = generation == listGeneration

    private fun nextThreadGeneration(): Long {
        threadGeneration += 1
        return threadGeneration
    }

    private fun isCurrentThread(generation: Long): Boolean = generation == threadGeneration
}
